import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { RowBetween } from '@pancakeswap/uikit'
import React, { Dispatch, ReactNode, SetStateAction } from 'react'
import { Bar, BarChart, CartesianGrid, XAxis } from 'recharts'
import { VolumeWindow } from '../../types'
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart'
import { cn } from '@/lib/utils'
import { Loading } from '@/components/ui/loading'

dayjs.extend(utc)

const DEFAULT_HEIGHT = 300

export type LineChartProps = {
  data: any[]
  color?: string | undefined
  height?: number | undefined
  minHeight?: number
  setValue?: Dispatch<SetStateAction<number | undefined>> // used for value on hover
  setLabel?: Dispatch<SetStateAction<string | undefined>> // used for label of value
  value?: number
  label?: string
  activeWindow?: VolumeWindow
  topLeft?: ReactNode | undefined
  topRight?: ReactNode | undefined
  bottomLeft?: ReactNode | undefined
  bottomRight?: ReactNode | undefined
} & React.HTMLAttributes<HTMLDivElement>

const chartConfig = {
  desktop: {
    label: 'Desktop',
    color: 'hsl(var(--chart-1))',
  },
} satisfies ChartConfig

const Chart = ({
  data,
  color = '#1FC7D4',
  setValue,
  setLabel,
  value,
  label,
  activeWindow,
  topLeft,
  topRight,
  bottomLeft,
  bottomRight,
  height,
  minHeight = DEFAULT_HEIGHT,
  ...rest
}: LineChartProps) => {
  const parsedValue = value
  const now = dayjs()

  return (
    <div
      {...rest}
      className={cn('flex flex-col', rest.className)}
      style={{
        minHeight: `${minHeight}px`,
      }}
    >
      <div className={'flex justify-between'}>
        {topLeft ?? null}
        {topRight ?? null}
      </div>
      {data?.length === 0 ? (
        <div className={'flex items-center justify-center grow'}>
          <Loading />
        </div>
      ) : (
        <div className={''}>
          <ChartContainer config={chartConfig} className={cn('w-full')} style={{ height }}>
            <BarChart
              data={data}
              onMouseLeave={() => {
                if (setLabel) setLabel(undefined)
                if (setValue) setValue(undefined)
              }}
            >
              <CartesianGrid vertical={false} horizontal={false} />
              <XAxis
                dataKey="time"
                tickLine={false}
                tickMargin={10}
                axisLine={false}
                tickFormatter={(time) => {
                  return dayjs(time).format(activeWindow === VolumeWindow.monthly ? 'MMM' : 'DD')
                }}
              />
              <ChartTooltip
                cursor={false}
                content={
                  <ChartTooltipContent
                    className={'hidden'}
                    formatter={(_, name, props) => {
                      if (setValue && parsedValue !== props.payload.value) {
                        setValue(props.payload.value)
                      }
                      const formattedTime = dayjs(props.payload.time).format('MMM D')
                      const formattedTimeDaily = dayjs(props.payload.time).format('MMM D, YYYY')
                      const formattedTimePlusWeek = dayjs(props.payload.time).add(1, 'week')
                      const formattedTimePlusMonth = dayjs(props.payload.time).add(1, 'month')

                      if (setLabel && label !== formattedTime) {
                        if (activeWindow === VolumeWindow.weekly) {
                          const isCurrent = formattedTimePlusWeek.isAfter(now)
                          setLabel(
                            `${formattedTime}-${
                              isCurrent ? now.format('MMM D, YYYY') : formattedTimePlusWeek.format('MMM D, YYYY')
                            }`,
                          )
                        } else if (activeWindow === VolumeWindow.monthly) {
                          const isCurrent = formattedTimePlusMonth.isAfter(now)
                          setLabel(
                            `${formattedTime}-${
                              isCurrent ? now.format('MMM D, YYYY') : formattedTimePlusMonth.format('MMM D, YYYY')
                            }`,
                          )
                        } else {
                          setLabel(formattedTimeDaily)
                        }
                      }
                      return 0
                    }}
                  />
                }
              />
              <Bar dataKey="value" className={'fill-zap'} radius={2} />
            </BarChart>
          </ChartContainer>
        </div>
      )}
      <RowBetween>
        {bottomLeft ?? null}
        {bottomRight ?? null}
      </RowBetween>
    </div>
  )
}

export default Chart
